/** トレーニング経過の数字の出し方(一覧と詳細で共通) */
import ToneValue from '@core/components/ui/ToneValue';
import { formatDecimal, NO_DATA } from '@core/utils/formatters';
import type { RecordEffect } from './training';

/** 1時間あたり取得(小数2桁。採用データのシートと同じ) */
export const fmtRate = (v: number | null | undefined) => formatDecimal(v, { digits: 2 });

/** 前の段階・面談前との差(+ を付ける。伸びたら緑の太字、下がったら赤) */
export function RateDiff({ value }: { value: number | null }) {
  if (value === null) return <>{NO_DATA}</>;
  // 表示する小数2桁で判定する(+0.004 を「+0.00(良い)」と出さない)
  const v = Math.round(value * 100) / 100;
  const text = `${v > 0 ? '+' : ''}${formatDecimal(v === 0 ? 0 : v, { digits: 2 })}`;
  return <ToneValue tone={v > 0 ? 'good' : v < 0 ? 'negative' : ''}>{text}</ToneValue>;
}

/** 面談の後が15hに届いていないときの注記「(12.4h時点)」(2026-10-09 発注者指示)。届いていれば空 */
export const partialNote = (e: Pick<RecordEffect, 'after' | 'afterComplete'>): string =>
  e.after && !e.afterComplete ? `(${formatDecimal(e.after.hours, { digits: 1 })}h時点)` : '';

/** 目標のある段階の件/h(2026-10-14): 目標以上は緑の太字、未満は赤字(印は付けない。発注者指示)。目標が無ければ色なし */
export function TargetRate({ value, target }: { value: number | null; target: number | null }) {
  if (value === null) return <>{NO_DATA}</>;
  if (target === null) return <>{fmtRate(value)}</>;
  // 表示する小数2桁で判定する
  const ok = Math.round(value * 100) / 100 >= target;
  return (
    <span title={`目標 ${fmtRate(target)}件/h ${ok ? '以上(達成)' : '未満(未達)'}`}>
      <ToneValue tone={ok ? 'good' : 'negative'} srText={ok ? '(目標達成)' : '(目標未達)'}>{fmtRate(value)}</ToneValue>
    </span>
  );
}

/** 秒(小数1桁) */
export const fmtSec = (v: number | null | undefined) => formatDecimal(v, { digits: 1, unit: '秒' });

/**
 * 通話時間Av・後処理時間Av・1時間あたりの通話回数(2026-10-20 発注者指示)。目標(給湯器の面談予定日の目標と同じ)を満たせば緑の太字、満たさなければ赤字。
 * higher = 大きいほど良い(通話回数)/ 小さいほど良い(通話時間・後処理時間)
 */
export function TargetValue({ value, target, higher, text, unit }: { value: number | null; target: number | null; higher: boolean; text: string; unit: string }) {
  if (value === null) return <>{NO_DATA}</>;
  if (target === null) return <>{text}</>;
  // 目標対比(TargetDiff)と同じ小数2桁で判定する(値と差で色が食い違わないように)
  const v = Math.round(value * 100) / 100;
  const ok = higher ? v >= target : v <= target;
  return (
    <span title={`目標 ${target}${unit} ${higher ? '以上' : '以下'}(${ok ? '達成' : '未達'})`}>
      <ToneValue tone={ok ? 'good' : 'negative'} srText={ok ? '(目標達成)' : '(目標未達)'}>{text}</ToneValue>
    </span>
  );
}
